import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import React from "react";

/**
 * Loading Component
 *
 * A reusable SVG-based loading spinner that supports multiple color variants.
 * Uses `class-variance-authority` (CVA) to manage theme-based color options.
 */
const loadingVariants = cva("inline-block animate-spin", {
  variants: {
    color: {
      primary:
        "text-[var(--primary-spinner-color)] fill-[var(--filled-base-main)]",
      white: "text-[var(--white-spinner-color)] fill-[var(--fill-base-white)]",
      danger:
        "text-[var(--danger-spinner-color)] fill-[var(--fill-red-solid-normal)]",
      gray: "text-[var(--gray-spinner-color)] fill-[var(--fill-gray-stronger)]",
    },
  },
  defaultVariants: {
    color: "primary",
  },
});

export interface ISVGProps
  extends Omit<React.SVGProps<SVGSVGElement>, "color">,
    VariantProps<typeof loadingVariants> {
  className?: string;
}

// SonarQube: role="status" used intentionally for accessibility.
// <output> is not suitable since this is a non-textual loading indicator.
export const Loading = ({ className, color, ...props }: ISVGProps) => {
  return (
    <output aria-live="polite">
      <svg
        aria-hidden="true"
        className={cn(
          "h-6 w-6",
          loadingVariants({ color: color ?? "primary" }),
          className,
        )}
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        {/* SVG paths */}
      </svg>
      <span className="sr-only">Loading...</span>
    </output>
  );
};
